import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { KeyRound, LogOut, Mail, Upload, MessageSquare, Eye, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { api, getAdminToken, setAdminToken, formatApiErrorDetail } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";

const CATEGORIES = ["Decoração", "Cosplay", "Colecionáveis", "Protótipos", "Organizadores", "Personalizado", "Outro"];

const AdminPanel = ({ open, onOpenChange, onAuthed }) => {
    const [token, setToken] = useState(getAdminToken());
    const [password, setPassword] = useState("");
    const [loggingIn, setLoggingIn] = useState(false);
    const [name, setName] = useState("");
    const [category, setCategory] = useState("");
    const [price, setPrice] = useState("");
    const [description, setDescription] = useState("");
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [messages, setMessages] = useState([]);
    const [orders, setOrders] = useState([]);
    const [visits, setVisits] = useState(null);
    const [loadingVisits, setLoadingVisits] = useState(false);
    const [period, setPeriod] = useState("7");
    const [customStart, setCustomStart] = useState("");
    const [customEnd, setCustomEnd] = useState("");
    const [formVersion, setFormVersion] = useState(0);
    const formRef = useRef(null);
    const qc = useQueryClient();

    const loadAdminData = async () => {
        try {
            const { data } = await api.get("/contact", {
                headers: { Authorization: `Bearer ${getAdminToken()}` },
            });
            setMessages(data.messages);
        } catch {
            setMessages([]);
        }
        try {
            const { data } = await api.get("/admin/orders", {
                headers: { Authorization: `Bearer ${getAdminToken()}` },
            });
            setOrders(data.orders);
        } catch {
            setOrders([]);
        }
    };

    const loadVisits = async (opts = {}) => {
        const useCustom = opts.custom && customStart && customEnd;
        setLoadingVisits(true);
        try {
            const params = useCustom
                ? { start: customStart, end: customEnd }
                : { days: Number(opts.period ?? period) };
            const { data } = await api.get("/admin/visits", {
                headers: { Authorization: `Bearer ${getAdminToken()}` },
                params,
            });
            setVisits(data);
        } catch {
            toast.error("Não foi possível carregar as visitas.");
        } finally {
            setLoadingVisits(false);
        }
    };

    const setPreset = (p) => {
        setPeriod(p);
        loadVisits({ period: p });
    };

    useEffect(() => {
        if (open && token) {
            loadAdminData();
            loadVisits();
        }
    }, [open, token]);

    const login = async (e) => {
        e.preventDefault();
        setLoggingIn(true);
        try {
            const { data } = await api.post("/admin/login", { password });
            setAdminToken(data.token);
            setToken(data.token);
            setPassword("");
            onAuthed?.();
            toast.success("Bem-vindo de volta à forja.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setLoggingIn(false);
        }
    };

    const MAX_PHOTOS = 5;

    const pickFiles = (e) => {
        const picked = Array.from(e.target.files || []);
        e.target.value = "";
        if (!picked.length) return;
        const next = [...files];
        for (const f of picked) {
            if (!(f.type || "").startsWith("image/")) {
                toast.error(`"${f.name}" não é uma imagem.`);
                continue;
            }
            if (f.size > 10 * 1024 * 1024) {
                toast.error(`"${f.name}" passa de 10MB.`);
                continue;
            }
            if (next.length >= MAX_PHOTOS) {
                toast.error(`Máximo de ${MAX_PHOTOS} fotos por peça.`);
                break;
            }
            next.push(f);
        }
        setFiles(next);
    };

    const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

    const upload = async (e) => {
        e.preventDefault();
        if (files.length === 0) {
            toast.error("Escolha de 1 a 5 fotos da peça.");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("name", name);
            fd.append("category", category);
            fd.append("price", price);
            fd.append("description", description);
            files.forEach((f) => fd.append("files", f));
            await api.post("/products/upload", fd, {
                headers: { Authorization: `Bearer ${getAdminToken()}`, "Content-Type": "multipart/form-data" },
            });
            qc.invalidateQueries({ queryKey: ["products"] });
            toast.success("Peça publicada no catálogo!");
            setName("");
            setCategory("");
            setPrice("");
            setDescription("");
            setFiles([]);
            setFormVersion((v) => v + 1);
            formRef.current?.reset();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setUploading(false);
        }
    };

    const logout = () => {
        setAdminToken(null);
        setToken(null);
        setMessages([]);
        toast("Sessão encerrada.");
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-lenis-prevent="true"
                className="max-h-[85vh] max-w-md overflow-y-auto border-forge-line bg-forge-panel text-forge-paper"
            >
                <DialogTitle className="font-display text-lg font-bold uppercase">Área do criador</DialogTitle>

                {!token ? (
                    <form onSubmit={login} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="admin-password" className="font-mono text-[11px] uppercase tracking-[0.2em] text-forge-dim">
                                Senha de acesso
                            </Label>
                            <Input
                                id="admin-password"
                                type="password"
                                data-testid="admin-password-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="border-forge-line bg-forge-ink focus-visible:ring-forge-yellow"
                                required
                            />
                        </div>
                        <Button
                            type="submit"
                            data-testid="admin-login-submit"
                            disabled={loggingIn}
                            className="w-full bg-forge-yellow font-mono text-xs uppercase tracking-[0.2em] text-forge-ink hover:bg-forge-yellow/90"
                        >
                            <KeyRound className="h-4 w-4" />
                            {loggingIn ? "Verificando..." : "Entrar"}
                        </Button>
                    </form>
                ) : (
                    <div className="space-y-8">
                        <form ref={formRef} onSubmit={upload} className="space-y-4" data-testid="admin-upload-form">
                            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-forge-yellow">Nova peça</p>
                            <div className="space-y-2">
                                <Label htmlFor="upload-name" className="font-mono text-[11px] uppercase tracking-[0.2em] text-forge-dim">
                                    Nome da peça
                                </Label>
                                <Input
                                    id="upload-name"
                                    data-testid="upload-name-input"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    className="border-forge-line bg-forge-ink focus-visible:ring-forge-yellow"
                                    placeholder="Ex.: Vaso Hexagonal"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label className="font-mono text-[11px] uppercase tracking-[0.2em] text-forge-dim">Categoria</Label>
                                    <Select value={category} onValueChange={setCategory}>
                                        <SelectTrigger data-testid="upload-category-select" className="border-forge-line bg-forge-ink focus:ring-forge-yellow">
                                            <SelectValue placeholder="Selecionar" />
                                        </SelectTrigger>
                                        <SelectContent className="border-forge-line bg-forge-panel text-forge-paper">
                                            {CATEGORIES.map((c) => (
                                                <SelectItem key={c} value={c}>
                                                    {c}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="upload-price" className="font-mono text-[11px] uppercase tracking-[0.2em] text-forge-dim">
                                        Preço (R$)
                                    </Label>
                                    <Input
                                        id="upload-price"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        required
                                        data-testid="upload-price-input"
                                        value={price}
                                        onChange={(e) => setPrice(e.target.value)}
                                        className="border-forge-line bg-forge-ink focus-visible:ring-forge-yellow"
                                        placeholder="39.90"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="upload-description" className="font-mono text-[11px] uppercase tracking-[0.2em] text-forge-dim">
                                    Descrição formatada
                                </Label>
                                <RichTextEditor
                                    value={description}
                                    resetKey={formVersion}
                                    onChange={setDescription}
                                    testId="upload-description-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="upload-file" className="font-mono text-[11px] uppercase tracking-[0.2em] text-forge-dim">
                                    Fotos da peça (até 5)
                                </Label>
                                <Input
                                    id="upload-file"
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    data-testid="upload-file-input"
                                    onChange={pickFiles}
                                    className="border-forge-line bg-forge-ink file:mr-3 file:border-0 file:bg-forge-yellow file:px-2 file:py-1 file:font-mono file:text-[10px] file:uppercase file:tracking-widest file:text-forge-ink"
                                />
                                {files.length > 0 && (
                                    <ul className="space-y-1" data-testid="upload-file-list">
                                        {files.map((f, i) => (
                                            <li
                                                key={`${f.name}-${i}`}
                                                data-testid={`upload-file-item-${i}`}
                                                className="flex items-center justify-between gap-2 border border-forge-line bg-forge-ink px-3 py-1.5 text-xs text-forge-paper"
                                            >
                                                <span className="truncate">
                                                    {i + 1}. {f.name} <span className="text-forge-dim">({Math.max(1, Math.round(f.size / 1024))}KB)</span>
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeFile(i)}
                                                    aria-label={`Remover ${f.name}`}
                                                    data-testid={`upload-file-remove-${i}`}
                                                    className="shrink-0 text-forge-dim transition-colors duration-300 hover:text-red-400"
                                                >
                                                    <X className="h-3.5 w-3.5" />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <Button
                                type="submit"
                                data-testid="upload-submit"
                                disabled={uploading}
                                className="w-full bg-forge-yellow font-mono text-xs uppercase tracking-[0.2em] text-forge-ink hover:bg-forge-yellow/90"
                            >
                                <Upload className="h-4 w-4" />
                                {uploading ? "Enviando..." : "Publicar peça"}
                            </Button>
                        </form>

                        <div className="space-y-3" data-testid="admin-messages-list">
                            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-forge-yellow">
                                <MessageSquare className="h-3.5 w-3.5" />
                                Mensagens recebidas ({messages.length})
                            </p>
                            {messages.length === 0 ? (
                                <p className="text-xs text-forge-dim">Nenhuma mensagem ainda.</p>
                            ) : (
                                <div className="max-h-56 space-y-3 overflow-y-auto pr-1" data-lenis-prevent="true">
                                    {messages.map((m) => (
                                        <div key={m.id} className="border border-forge-line bg-forge-ink p-3 text-xs" data-testid="admin-message-item">
                                            <p className="font-mono text-forge-yellow">{m.name}</p>
                                            <p className="text-forge-dim">{m.email}</p>
                                            <p className="mt-2 leading-relaxed text-forge-paper">{m.message}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="space-y-3" data-testid="admin-orders-list">
                            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-forge-yellow">
                                <MessageSquare className="h-3.5 w-3.5" />
                                Pedidos ({orders.length})
                            </p>
                            {orders.length === 0 ? (
                                <p className="text-xs text-forge-dim">Nenhum pedido ainda.</p>
                            ) : (
                                <div className="max-h-56 space-y-3 overflow-y-auto pr-1" data-lenis-prevent="true">
                                    {orders.map((o) => (
                                        <div key={o.id} className="border border-forge-line bg-forge-ink p-3 text-xs" data-testid="admin-order-item">
                                            <div className="flex items-center justify-between gap-2">
                                                <p className="font-mono text-forge-yellow">{o.user_name || o.user_email}</p>
                                                <span className={`px-2 py-0.5 font-mono text-[10px] uppercase ${o.status === "paid" ? "bg-green-500/15 text-green-400" : "bg-forge-yellow/15 text-forge-yellow"}`}>
                                                    {o.status === "paid" ? "Pago" : "Pendente"}
                                                </span>
                                            </div>
                                            <p className="mt-2 leading-relaxed text-forge-paper">
                                                {o.items.map((i) => `${i.quantity}x ${i.name}`).join(" · ")}
                                            </p>
                                            <p className="mt-1 font-mono text-forge-dim">
                                                {(o.total_cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="space-y-3" data-testid="admin-visits-section">
                            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-forge-yellow">
                                <Eye className="h-3.5 w-3.5" />
                                Visitas ao site
                            </p>
                            <div className="flex flex-wrap gap-1" data-testid="visits-period-buttons">
                                {[["1", "Hoje"], ["7", "7 dias"], ["30", "30 dias"], ["90", "90 dias"]].map(([v, label]) => (
                                    <button
                                        key={v}
                                        type="button"
                                        data-testid={`visits-period-${v}`}
                                        disabled={loadingVisits}
                                        onClick={() => setPreset(v)}
                                        className={`border px-2 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors duration-300 ${
                                            period === v
                                                ? "border-forge-yellow bg-forge-yellow/15 text-forge-yellow"
                                                : "border-forge-line text-forge-dim hover:text-forge-paper"
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                            <div className="flex items-center gap-2" data-testid="visits-custom-range">
                                <Input
                                    type="date"
                                    data-testid="visits-start-input"
                                    value={customStart}
                                    onChange={(e) => setCustomStart(e.target.value)}
                                    className="h-8 border-forge-line bg-forge-ink text-xs text-forge-paper"
                                />
                                <span className="font-mono text-[10px] uppercase text-forge-dim">até</span>
                                <Input
                                    type="date"
                                    data-testid="visits-end-input"
                                    value={customEnd}
                                    onChange={(e) => setCustomEnd(e.target.value)}
                                    className="h-8 border-forge-line bg-forge-ink text-xs text-forge-paper"
                                />
                                <Button
                                    type="button"
                                    size="sm"
                                    data-testid="visits-apply-custom"
                                    disabled={!(customStart && customEnd) || loadingVisits}
                                    onClick={() => loadVisits({ custom: true })}
                                    className="h-8 bg-forge-yellow font-mono text-[10px] uppercase tracking-widest text-forge-ink hover:bg-forge-yellow/90"
                                >
                                    Ver
                                </Button>
                            </div>
                            {visits ? (
                                <div className="space-y-3" data-testid="visits-stats">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="border border-forge-line bg-forge-ink p-3 text-center">
                                            <p className="font-display text-2xl font-bold text-forge-yellow" data-testid="visits-total">
                                                {visits.views}
                                            </p>
                                            <p className="font-mono text-[10px] uppercase tracking-widest text-forge-dim">Visitas</p>
                                        </div>
                                        <div className="border border-forge-line bg-forge-ink p-3 text-center">
                                            <p className="font-display text-2xl font-bold text-forge-yellow" data-testid="visits-unique">
                                                {visits.visitors}
                                            </p>
                                            <p className="font-mono text-[10px] uppercase tracking-widest text-forge-dim">Visitantes únicos</p>
                                        </div>
                                    </div>
                                    {visits.series.length > 1 &&
                                        (() => {
                                            const maxV = Math.max(...visits.series.map((s) => s.views), 1);
                                            return (
                                                <div className="flex h-16 items-end gap-1 border border-forge-line bg-forge-ink p-2" data-testid="visits-chart">
                                                    {visits.series.map((s) => (
                                                        <div
                                                            key={s.day}
                                                            title={`${s.day}: ${s.views} visita(s)`}
                                                            className="flex-1 bg-forge-yellow/70 transition-colors duration-300 hover:bg-forge-yellow"
                                                            style={{ height: `${Math.max(4, (s.views / maxV) * 100)}%` }}
                                                        />
                                                    ))}
                                                </div>
                                            );
                                        })()}
                                    <div className="space-y-1" data-testid="visits-top-list">
                                        <p className="font-mono text-[10px] uppercase tracking-widest text-forge-dim">Páginas mais vistas no período</p>
                                        {visits.top.length === 0 ? (
                                            <p className="text-xs text-forge-dim">Nenhuma visita registrada ainda.</p>
                                        ) : (
                                            visits.top.map((t) => (
                                                <div key={t.path} data-testid="visits-top-item" className="flex items-center justify-between border border-forge-line bg-forge-ink px-3 py-1.5 text-xs">
                                                    <span className="text-forge-paper">{t.path}</span>
                                                    <span className="font-mono text-forge-yellow">{t.views}</span>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                    <p className="font-mono text-[10px] leading-relaxed text-forge-dim">
                                        Período: {visits.start} a {visits.end} — contagem a partir da ativação do recurso.
                                    </p>
                                </div>
                            ) : (
                                <p className="text-xs text-forge-dim">{loadingVisits ? "Carregando..." : "—"}</p>
                            )}
                        </div>

                        <Button
                            variant="outline"
                            data-testid="admin-logout-button"
                            onClick={logout}
                            className="w-full border-forge-line bg-transparent font-mono text-xs uppercase tracking-[0.2em] text-forge-dim hover:bg-forge-ink hover:text-forge-paper"
                        >
                            <LogOut className="h-4 w-4" />
                            Sair
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default AdminPanel;
